import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X } from "lucide-react";
import {
  BodyNormalBold,
  BodySmallMedium,
  BodySmallRegular,
} from "../ui/Typography";
import {
  commissionService,
  PackageType,
} from "../../api/services/commission/commissionService";
import toast from "react-hot-toast";

interface PackageManagementProps {
  setShowCreate: React.Dispatch<React.SetStateAction<boolean>>;
}
export const PackageManagement: React.FC<PackageManagementProps> = ({
  setShowCreate,
}) => {
  const [packages, setPackages] = useState<PackageType[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState<PackageType | null>(
    null,
  );
  const [deletingPackage, setDeletingPackage] = useState<PackageType | null>(
    null,
  );
  const [formData, setFormData] = useState({
    name: "",
    betAmountPerPoint: 0,
    description: "",
  });

  useEffect(() => {
    setShowCreate(showCreateForm);
  }, [showCreateForm, setShowCreate]);

  useEffect(() => {
    let isMounted = true;

    const loadPackages = async () => {
      try {
        setLoading(true);
        const packageData = await commissionService.getPackageTypes();
        if (isMounted) {
          setPackages(packageData);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error loading packages:", error);
          toast.error("Failed to load packages");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPackages();

    return () => {
      isMounted = false;
    };
  }, []);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const packageData = await commissionService.getPackageTypes();
      setPackages(packageData);
    } catch (error) {
      console.error("Error loading packages:", error);
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (editingPackage) {
        // Update existing package
        await commissionService.updatePackageType(editingPackage.id, {
          name: formData.name,
          betAmountPerPoint: formData.betAmountPerPoint,
          description: formData.description || undefined,
        });
        toast.success("Package type updated successfully");
        setEditingPackage(null);
      } else {
        // Create new package
        await commissionService.createPackageType({
          name: formData.name,
          betAmountPerPoint: formData.betAmountPerPoint,
          description: formData.description || undefined,
        });
        toast.success("Package type created successfully");
      }

      setFormData({ name: "", betAmountPerPoint: 0, description: "" });
      setShowCreateForm(false);
      loadPackages();
    } catch (error) {
      console.error(
        editingPackage ? "Error updating package:" : "Error creating package:",
        error,
      );
      toast.error(
        editingPackage
          ? "Failed to update package"
          : "Failed to create package",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (pkg: PackageType) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      betAmountPerPoint: pkg.betAmountPerPoint,
      description: pkg.description || "",
    });
    setShowCreateForm(true);
  };

  const handleDelete = async () => {
    if (!deletingPackage) return;

    try {
      setLoading(true);
      await commissionService.deletePackageType(deletingPackage.id);
      toast.success("Package type deleted successfully");
      setDeletingPackage(null);
      loadPackages();
    } catch (error) {
      console.error("Error deleting package:", error);
      toast.error("Failed to delete package. It may be in use.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowCreateForm(false);
    setEditingPackage(null);
    setFormData({ name: "", betAmountPerPoint: 0, description: "" });
  };

  const formatCurrency = commissionService.formatCurrency;

  return (
    <div className="">
      <div className="bg-gray-800 rounded-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header with Add Package Button */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <BodyNormalBold className="text-white">
                Package Types
              </BodyNormalBold>
              <BodySmallRegular className="text-gray-400">
                Manage commission package types and their betting requirements
              </BodySmallRegular>
            </div>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Package
            </button>
          </div>

          {/* // In PackageManagement.tsx */}
          {showCreateForm && (
            <div
              className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowCreateForm(false)}
            >
              <div
                className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl border border-gray-700 transform transition-all"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-white text-lg font-medium">
                    {editingPackage
                      ? "Edit Package Type"
                      : "Create New Package Type"}
                  </h3>
                  <button
                    onClick={handleCancelForm}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Package Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="w-full p-2 border border-gray-600 rounded-lg bg-gray-700 text-white"
                      placeholder="e.g., Premium, VIP"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Bet Amount Per Point *
                    </label>
                    <input
                      type="number"
                      value={formData.betAmountPerPoint || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          betAmountPerPoint: Number(e.target.value),
                        }))
                      }
                      className="w-full p-2 border border-gray-600 rounded-lg bg-gray-700 text-white"
                      placeholder="5000"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      className="w-full p-2 border border-gray-600 rounded-lg bg-gray-700 text-white"
                      placeholder="Package description"
                      rows={3}
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleCancelForm}
                      className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-primary hover:bg-primary/90 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      {loading
                        ? editingPackage
                          ? "Updating..."
                          : "Creating..."
                        : editingPackage
                          ? "Update Package"
                          : "Create Package"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
          {/* Packages List */}
          <div className="bg-gray-900 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-800 border-b border-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <BodySmallMedium className="text-gray-400">
                      Name
                    </BodySmallMedium>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <BodySmallMedium className="text-gray-400">
                      Bet Amount/Point
                    </BodySmallMedium>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <BodySmallMedium className="text-gray-400">
                      Description
                    </BodySmallMedium>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <BodySmallMedium className="text-gray-400">
                      Status
                    </BodySmallMedium>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <BodySmallMedium className="text-gray-400">
                      Actions
                    </BodySmallMedium>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <BodySmallRegular className="text-gray-400">
                        Loading packages...
                      </BodySmallRegular>
                    </td>
                  </tr>
                ) : packages.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <BodySmallRegular className="text-gray-400">
                        No packages found. Create your first package type.
                      </BodySmallRegular>
                    </td>
                  </tr>
                ) : (
                  packages.map((pkg) => (
                    <tr
                      key={pkg.id}
                      className="border-b border-gray-700 hover:bg-gray-800/50"
                    >
                      <td className="px-4 py-3">
                        <BodySmallMedium className="text-white">
                          {pkg.name}
                        </BodySmallMedium>
                      </td>
                      <td className="px-4 py-3">
                        <BodySmallMedium className="text-yellow-400">
                          {formatCurrency(pkg.betAmountPerPoint)}
                        </BodySmallMedium>
                      </td>
                      <td className="px-4 py-3">
                        <BodySmallRegular className="text-gray-300">
                          {pkg.description || "-"}
                        </BodySmallRegular>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            pkg.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {pkg.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEdit(pkg)}
                            className="p-1 hover:bg-gray-600 rounded transition-colors"
                            title="Edit Package"
                          >
                            <Edit className="w-4 h-4 text-gray-400" />
                          </button>
                          <button
                            onClick={() => setDeletingPackage(pkg)}
                            className="p-1 hover:bg-gray-600 rounded transition-colors"
                            title="Delete Package"
                          >
                            <Trash2 className="w-4 h-4 text-gray-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deletingPackage && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setDeletingPackage(null)}
        >
          <div
            className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-medium">Confirm Delete</h3>
              <button
                onClick={() => setDeletingPackage(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-300 mb-6">
              Are you sure you want to delete the package type &quot;
              {deletingPackage.name}&quot;? This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingPackage(null)}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
              >
                {loading ? "Deleting..." : "Delete Package"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
